/**
 * DataPreviewModal - Modal for reviewing and validating uploaded data
 * Shows schema mapping, data preview, validation errors, and inline editing
 */

import React, { useState, useEffect } from 'react';
import uploadService, { PreviewData } from '../services/uploadService';

interface DataPreviewModalProps {
  uploadId: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void; // Optional - only show import button if provided
}

interface ValidationError {
  row: number;
  field: string;
  error: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  value?: any;
}

const DataPreviewModal: React.FC<DataPreviewModalProps> = ({
  uploadId,
  isOpen,
  onClose,
  onConfirm
}) => {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userMappings, setUserMappings] = useState<Record<string, string>>({});
  const [selectedTemplateType, setSelectedTemplateType] = useState<string>('portfolio');
  const [editingCell, setEditingCell] = useState<{ row: number; field: string } | null>(null);
  const [editedData, setEditedData] = useState<Record<string, any>>({});
  const [activeTab, setActiveTab] = useState<'mapping' | 'preview' | 'errors'>('mapping');

  useEffect(() => {
    if (isOpen && uploadId) {
      loadPreviewData();
    }
  }, [isOpen, uploadId]);

  const loadPreviewData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await uploadService.getPreview(uploadId);
      setPreviewData(data);
      setUserMappings(data.auto_mappings || {});
      setSelectedTemplateType(data.detected_template_type || 'portfolio');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preview data');
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (originalField: string, newMapping: string) => {
    setUserMappings(prev => ({
      ...prev,
      [originalField]: newMapping
    }));
  };

  const handleSaveMapping = async () => {
    try {
      const templateOverride = selectedTemplateType !== previewData?.detected_template_type ? selectedTemplateType : undefined;
      await uploadService.updateMapping(uploadId, userMappings, templateOverride);
      // Reload preview data to see updated validation
      await loadPreviewData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save mapping');
    }
  };

  const handleConfirm = async () => {
    try {
      // Save any pending mapping changes
      const templateOverride = selectedTemplateType !== previewData?.detected_template_type ? selectedTemplateType : undefined;
      await uploadService.updateMapping(uploadId, userMappings, templateOverride);

      // Confirm the upload for final processing
      await uploadService.confirmUpload(uploadId);

      onConfirm?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm upload');
    }
  };

  const handleCellEdit = (row: number, field: string, value: any) => {
    const key = `${row}_${field}`;
    setEditedData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getErrorsForRow = (rowIndex: number): ValidationError[] => {
    if (!previewData?.validation_errors) return [];
    return previewData.validation_errors.filter(err => err.row === rowIndex) as ValidationError[];
  };

  const getFieldDescription = (fieldName: string): string => {
    const descriptions: Record<string, string> = {
      // Portfolio fields
      client_id: "Client/Account identifier",
      isin: "International Securities Identification Number",
      quantity: "Number of units/shares held",
      market_value: "Current market value of position",
      currency: "Currency of the position",
      position_date: "Date of the position/valuation",
      asset_name: "Name/description of the security",
      asset_type: "Type/category of the asset",
      bank: "Custodian bank",
      portfolio: "Portfolio/account name",
      // KYC fields
      first_name: "Client's first name",
      last_name: "Client's last name",
      birth_date: "Date of birth",
      nationality: "Client's nationality",
      tax_id: "Tax identification number",
      address: "Street address",
      city: "City of residence",
      postal_code: "Postal/ZIP code",
      country: "Country of residence",
      phone: "Phone number",
      email: "Email address",
      risk_profile: "Investment risk tolerance",
      investment_experience: "Years of investment experience",
      income: "Annual income",
      net_worth: "Total net worth",
      // ESG fields
      company_name: "Company name",
      esg_score: "ESG composite score",
      environmental_score: "Environmental score",
      social_score: "Social score",
      governance_score: "Governance score",
      carbon_footprint: "Carbon footprint measurement",
      carbon_intensity: "Carbon intensity ratio",
      climate_risk: "Climate risk assessment",
      reporting_date: "Date of ESG report",
      // Generic field types
      id_field: "🔑 Identifier/ID field (auto-detected)",
      name_field: "📝 Name/description field (auto-detected)",
      date_field: "📅 Date/time field (auto-detected)",
      amount_field: "💰 Amount/currency field (auto-detected)",
      percentage_field: "📊 Percentage/rate field (auto-detected)", 
      quantity_field: "🔢 Quantity/count field (auto-detected)",
      status_field: "📋 Status/category field (auto-detected)",
      currency_field: "💱 Currency code field (auto-detected)",
      unknown_field: "❓ Field type could not be determined"
    };
    return descriptions[fieldName] || fieldName;
  };

  const getRequiredFieldsByTemplate = (templateType: string): string[] => {
    switch (templateType) {
      case 'kyc':
        return ['client_id', 'first_name', 'last_name', 'birth_date', 'nationality', 'tax_id'];
      case 'esg':
        return ['client_id', 'company_name', 'esg_score', 'reporting_date'];
      case 'compliance':
        return ['client_id', 'transaction_id', 'transaction_date', 'instrument'];
      case 'risk':
        return ['client_id', 'portfolio_id', 'risk_date'];
      case 'client_master':
        return ['client_id', 'name', 'domicile', 'client_type', 'finsa_category', 'kyc_status', 'onboard_date'];
      case 'compliance_monitoring':
        return ['task_id', 'obligation', 'frequency', 'next_due_date', 'responsible', 'status'];
      case 'data_protection':
        return ['process_id', 'processing_activity', 'data_categories', 'lawful_basis', 'storage_location', 'retention_period'];
      case 'esg_climate':
        return ['isin', 'emissions_tco2e', 'temperature_alignment'];
      case 'mandate_terms':
        return ['mandate_id', 'client_id', 'mandate_type', 'discretionary_limit'];
      case 'multicustodian_positions':
        return ['client_id', 'account', 'isin', 'quantity', 'mv_chf'];
      case 'operational_readiness':
        return ['artifact_id', 'artifact', 'status', 'last_updated'];
      case 'order_blotter':
        return ['date', 'time', 'custodian', 'account', 'isin', 'side', 'quantity', 'currency'];
      case 'performance_fee':
        return ['client_id', 'period', 'twr_percent', 'mwr_percent', 'mgmt_fee_percent', 'perf_fee_percent', 'fee_accrued_chf'];
      case 'suitability_risk':
        return ['client_id', 'knowledge_equities', 'knowledge_funds', 'capacity_for_loss', 'risk_score', 'risk_category'];
      case 'generic':
        return []; // Generic template has no strictly required fields
      case 'portfolio':
      default:
        return ['client_id', 'isin', 'quantity', 'market_value', 'currency'];
    }
  };

  const getAvailableFieldsForTemplate = (templateType: string): string[] => {
    switch (templateType) {
      case 'kyc':
        return [
          'client_id', 'first_name', 'last_name', 'birth_date', 'nationality', 'tax_id',
          'address', 'city', 'postal_code', 'country', 'phone', 'email',
          'risk_profile', 'investment_experience', 'income', 'net_worth'
        ];
      case 'esg':
        return [
          'client_id', 'company_name', 'esg_score', 'environmental_score', 'social_score',
          'governance_score', 'carbon_footprint', 'carbon_intensity', 'climate_risk', 'reporting_date'
        ];
      case 'compliance':
        return [
          'client_id', 'transaction_id', 'transaction_date', 'instrument',
          'amount', 'currency'
        ];
      case 'risk':
        return [
          'client_id', 'portfolio_id', 'var_1d', 'var_10d', 'volatility', 'risk_date'
        ];
      case 'client_master':
        return [
          'client_id', 'name', 'domicile', 'client_type', 'finsa_category', 
          'kyc_status', 'onboard_date'
        ];
      case 'compliance_monitoring':
        return [
          'task_id', 'obligation', 'frequency', 'next_due_date', 
          'responsible', 'status'
        ];
      case 'data_protection':
        return [
          'process_id', 'processing_activity', 'data_categories', 
          'lawful_basis', 'storage_location', 'retention_period'
        ];
      case 'esg_climate':
        return [
          'isin', 'emissions_tco2e', 'temperature_alignment'
        ];
      case 'mandate_terms':
        return [
          'mandate_id', 'client_id', 'mandate_type', 'discretionary_limit',
          'restricted_securities', 'rebalancing_band'
        ];
      case 'multicustodian_positions':
        return [
          'client_id', 'account', 'isin', 'quantity', 'mv_chf'
        ];
      case 'operational_readiness':
        return [
          'artifact_id', 'artifact', 'status', 'last_updated'
        ];
      case 'order_blotter':
        return [
          'date', 'time', 'custodian', 'account', 'isin', 'side', 'quantity',
          'limit_price', 'currency', 'execution_notes', 'approval'
        ];
      case 'performance_fee':
        return [
          'client_id', 'period', 'twr_percent', 'mwr_percent', 'mgmt_fee_percent',
          'perf_fee_percent', 'fee_accrued_chf'
        ];
      case 'suitability_risk':
        return [
          'client_id', 'knowledge_equities', 'knowledge_funds', 'capacity_for_loss',
          'risk_score', 'risk_category'
        ];
      case 'generic':
        return [
          'id_field', 'name_field', 'date_field', 'amount_field', 'percentage_field',
          'quantity_field', 'status_field', 'currency_field', 'unknown_field'
        ];
      case 'portfolio':
      default:
        return [
          'client_id', 'isin', 'quantity', 'market_value', 'currency',
          'position_date', 'asset_name', 'asset_type', 'bank', 'portfolio'
        ];
    }
  };
  
  const requiredFields = getRequiredFieldsByTemplate(selectedTemplateType);
  const canProceed = previewData ? previewData.error_percentage <= 5 : false;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">
              📊 Data Preview & Validation
            </h2>
            {previewData && (
              <>
                <p className="text-sm text-gray-600 mt-1">
                  {previewData.total_rows} rows • {previewData.validation_errors?.length || 0} issues
                  <span className="ml-2 px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 capitalize">
                    {selectedTemplateType} template
                  </span>
                  {previewData.template_confidence < 0.8 && (
                    <span className="ml-1 px-2 py-1 rounded text-xs bg-orange-100 text-orange-800">
                      {Math.round(previewData.template_confidence * 100)}% confidence
                    </span>
                  )}
                  {previewData.error_percentage > 0 && (
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                      previewData.error_percentage <= 5 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {previewData.error_percentage.toFixed(1)}% error rate
                    </span>
                  )}
                </p>
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4"></div>
              <p className="text-gray-600">Loading preview data...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={loadPreviewData}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : previewData ? (
          <>
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('mapping')}
                className={`px-6 py-3 font-medium text-sm border-b-2 ${
                  activeTab === 'mapping'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                🔗 Field Mapping ({Object.keys(previewData.auto_mappings || {}).length})
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className={`px-6 py-3 font-medium text-sm border-b-2 ${
                  activeTab === 'preview'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                👀 Data Preview ({previewData.total_rows} rows)
              </button>
              <button
                onClick={() => setActiveTab('errors')}
                className={`px-6 py-3 font-medium text-sm border-b-2 ${
                  activeTab === 'errors'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                ⚠️ Issues ({previewData.validation_errors?.length || 0})
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {activeTab === 'mapping' && (
                <div className="h-full overflow-y-auto p-6">
                  <div className="mb-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-medium mb-2">Schema Field Mapping</h3>
                          <p className="text-sm text-gray-600">
                            {selectedTemplateType === 'generic' 
                              ? 'AI-powered field detection has analyzed your file structure. Review the auto-detected field types below.'
                              : `Review and adjust how your file columns are mapped to ${selectedTemplateType} fields.`
                            }
                          </p>
                        </div>
                      
                      {/* Template Type Selector */}
                      <div className="ml-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Template Type
                        </label>
                        <select
                          value={selectedTemplateType}
                          onChange={(e) => setSelectedTemplateType(e.target.value)}
                          className="border border-gray-300 rounded px-3 py-2 text-sm min-w-32"
                        >
                          <option value="portfolio">Portfolio</option>
                          <option value="kyc">KYC</option>
                          <option value="esg">ESG</option>
                          <option value="compliance">Compliance</option>
                          <option value="risk">Risk</option>
                          <option value="client_master">Client Master Onboarding</option>
                          <option value="compliance_monitoring">Compliance Monitoring</option>
                          <option value="data_protection">Data Protection</option>
                          <option value="esg_climate">ESG Climate Scores</option>
                          <option value="mandate_terms">Mandate Terms Tracker</option>
                          <option value="multicustodian_positions">MultiCustodian Positions</option>
                          <option value="operational_readiness">Operational Readiness</option>
                          <option value="order_blotter">Order Blotter Trade Tickets</option>
                          <option value="performance_fee">Performance Fee Calculation</option>
                          <option value="suitability_risk">Suitability Risk Profiling</option>
                          <option value="generic">Generic (Auto-detect fields)</option>
                        </select>
                        {selectedTemplateType !== previewData.detected_template_type && (
                          <p className="text-xs text-orange-600 mt-1">
                            Override (detected: {previewData.detected_template_type})
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Generic Template Info Box */}
                  {selectedTemplateType === 'generic' && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <div className="text-blue-500 text-lg">🤖</div>
                        <div>
                          <h4 className="text-sm font-medium text-blue-900 mb-1">
                            Generic Template Active
                          </h4>
                          <p className="text-xs text-blue-700">
                            Our AI has analyzed your file and automatically detected field types. 
                            Review the suggestions below and adjust any that don't look correct. 
                            This helps us handle any Excel file format!
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Mapped Fields */}
                    {Object.entries(previewData.auto_mappings || {}).map(([originalField, mappedField]) => (
                      <div key={originalField} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                                {originalField}
                              </span>
                              <span className="text-gray-400">→</span>
                              <select
                                value={userMappings[originalField] || mappedField}
                                onChange={(e) => handleMappingChange(originalField, e.target.value)}
                                className="border border-gray-300 rounded px-3 py-1 text-sm"
                              >
                                <option value="">Unmapped</option>
                                {getAvailableFieldsForTemplate(selectedTemplateType).map(field => (
                                  <option key={field} value={field}>
                                    {field} {requiredFields.includes(field) && '*'}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {getFieldDescription(userMappings[originalField] || mappedField)}
                            </p>
                          </div>
                          <div className="ml-4">
                            {requiredFields.includes(userMappings[originalField] || mappedField) && (
                              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Required</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Unmapped Fields */}
                    {previewData.unmapped_fields && previewData.unmapped_fields.length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-medium text-gray-900 mb-3">Unmapped Fields</h4>
                        <div className="space-y-2">
                          {previewData.unmapped_fields.map(field => (
                            <div key={field} className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                              <span className="font-mono text-sm bg-white px-2 py-1 rounded border">
                                {field}
                              </span>
                              <span className="text-gray-400">→</span>
                              <select
                                value={userMappings[field] || ''}
                                onChange={(e) => handleMappingChange(field, e.target.value)}
                                className="border border-gray-300 rounded px-3 py-1 text-sm"
                              >
                                <option value="">Skip this field</option>
                                {requiredFields.map(reqField => (
                                  <option key={reqField} value={reqField}>
                                    {reqField} {requiredFields.includes(reqField) && '*'}
                                  </option>
                                ))}
                                <option value="asset_name">asset_name</option>
                                <option value="asset_type">asset_type</option>
                                <option value="bank">bank</option>
                                <option value="portfolio">portfolio</option>
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end mt-6">
                      <button
                        onClick={handleSaveMapping}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      >
                        Apply Mapping Changes
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'preview' && (
                <div className="h-full overflow-auto p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium mb-2">Sample Data Preview</h3>
                    <p className="text-sm text-gray-600">
                      First 100 rows of your data with current field mapping applied
                    </p>
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Row
                            </th>
                            {previewData.sample_rows?.[0] && Object.keys(previewData.sample_rows[0]).map(field => (
                              <th key={field} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {field}
                                {Object.values(previewData.auto_mappings || {}).includes(field) && (
                                  <span className="ml-1 text-blue-600">✓</span>
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {previewData.sample_rows?.slice(0, 50).map((row, index) => {
                            const rowErrors = getErrorsForRow(index);
                            return (
                              <tr key={index} className={rowErrors.length > 0 ? 'bg-red-50' : ''}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {index + 1}
                                  {rowErrors.length > 0 && (
                                    <span className="ml-1 text-red-500">⚠</span>
                                  )}
                                </td>
                                {Object.entries(row).map(([field, value]) => (
                                  <td
                                    key={field}
                                    className={`px-3 py-2 text-sm ${
                                      rowErrors.some(err => err.field === field)
                                        ? 'text-red-700 bg-red-100'
                                        : 'text-gray-900'
                                    }`}
                                  >
                                    {editingCell?.row === index && editingCell?.field === field ? (
                                      <input
                                        type="text"
                                        value={editedData[`${index}_${field}`] ?? value}
                                        onChange={(e) => handleCellEdit(index, field, e.target.value)}
                                        onBlur={() => setEditingCell(null)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            setEditingCell(null);
                                          }
                                        }}
                                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                        autoFocus
                                      />
                                    ) : (
                                      <span
                                        onClick={() => setEditingCell({ row: index, field })}
                                        className="cursor-pointer hover:bg-gray-100 rounded px-1"
                                      >
                                        {String(value || '')}
                                      </span>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'errors' && (
                <div className="h-full overflow-y-auto p-6">
                  <div className="mb-6">
                    <h3 className="text-lg font-medium mb-2">Data Quality Issues</h3>
                    <p className="text-sm text-gray-600">
                      Review and address data quality issues before proceeding
                    </p>
                  </div>

                  {previewData.validation_errors && previewData.validation_errors.length > 0 ? (
                    <div className="space-y-4">
                      {['critical', 'high', 'medium', 'low'].map(severity => {
                        const errorsOfType = previewData.validation_errors!.filter(
                          (err: any) => err.severity === severity
                        ) as ValidationError[];
                        
                        if (errorsOfType.length === 0) return null;

                        const severityColors = {
                          critical: 'bg-red-100 border-red-500 text-red-900',
                          high: 'bg-orange-100 border-orange-500 text-orange-900',
                          medium: 'bg-yellow-100 border-yellow-500 text-yellow-900',
                          low: 'bg-blue-100 border-blue-500 text-blue-900'
                        };

                        return (
                          <div key={severity}>
                            <h4 className={`font-medium mb-3 capitalize ${
                              severity === 'critical' ? 'text-red-900' :
                              severity === 'high' ? 'text-orange-900' :
                              severity === 'medium' ? 'text-yellow-900' :
                              'text-blue-900'
                            }`}>
                              {severity} Issues ({errorsOfType.length})
                            </h4>
                            <div className="space-y-2">
                              {errorsOfType.map((error: any, index) => (
                                <div
                                  key={index}
                                  className={`p-3 border-l-4 ${severityColors[severity as keyof typeof severityColors]}`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-medium">Row {error.row + 1}: {error.error}</p>
                                      <p className="text-sm opacity-75">
                                        Field: {error.field} | Value: {String(error.value || 'N/A')}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-green-500 text-6xl mb-4">✅</div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Issues Found</h3>
                      <p className="text-gray-600">Your data passed all validation checks!</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="border-t border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{previewData.total_rows}</span> rows total
                  {previewData.validation_errors && previewData.validation_errors.length > 0 && (
                    <>
                      {' • '}
                      <span className="font-medium">{previewData.validation_errors.length}</span> issues
                      {' • '}
                      <span className={`font-medium ${
                        previewData.error_percentage <= 5 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {previewData.error_percentage.toFixed(1)}% error rate
                      </span>
                    </>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  >
                    {onConfirm ? 'Cancel' : 'Close'}
                  </button>
                  {onConfirm && (
                    canProceed ? (
                      <button
                        onClick={handleConfirm}
                        className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Proceed with Import
                        {previewData.validation_errors && previewData.validation_errors.length > 0 && (
                          <span className="ml-2 text-sm opacity-75">
                            (with {previewData.validation_errors.length} issues)
                          </span>
                        )}
                      </button>
                    ) : (
                      <div className="text-right">
                        <button
                          disabled
                          className="px-6 py-2 bg-gray-300 text-gray-500 rounded cursor-not-allowed"
                        >
                          Fix Critical Issues First
                        </button>
                        <p className="text-xs text-red-600 mt-1">
                          Error rate too high ({previewData.error_percentage.toFixed(1)}% &gt; 5%)
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default DataPreviewModal;